"use client";

import { useAuthState } from "@/contexts/AuthStateProvider";
import { useQuery } from "@tanstack/react-query";

import {
  getActivePersonality,
  getDefaultAssistantIdentity,
} from "@/hooks/assistant/assistantApi";

/**
 * Cache key for the signed-out default assistant. Exported because the admin
 * "Make default" action must invalidate exactly this key — a second copy of the
 * literal is how a stale default survives the change that was meant to move it.
 */
export const DEFAULT_ASSISTANT_IDENTITY_QUERY_KEY = [
  "default-assistant-identity",
] as const;

/** Name and avatar of the assistant to show for the current viewer. */
export interface AssistantIdentity {
  /** Null while loading or when no assistant could be resolved — never a hardcoded name. */
  name: string | null;
  /** Uploaded icon URL; null means SmartAssistantAvatar picks the bundled art for `name`. */
  icon: string | null;
  isLoading: boolean;
}

// Freshness follows staticProfile (queryProfiles.ts); the client defaults already
// match it, so the assistant identity needs no overrides.

interface ActivePersonalityResponse {
  assistantPersonality?: { name?: string | null; icon?: string | null } | null;
}

/**
 * Resolves which assistant the viewer sees.
 *
 * Signed in: their selection, falling back server-side to the default assistant.
 * Signed out: the default assistant, via the public settings route.
 *
 * Callers must render the loading state rather than substituting a name — a
 * literal fallback is what made every surface say "Marvin" regardless of the
 * configured default.
 */
export function useAssistantIdentity(): AssistantIdentity {
  const { isSignedIn, isLoaded } = useAuthState();

  const memberQuery = useQuery({
    queryKey: ["user-selected-personality"],
    queryFn: () => getActivePersonality(),
    enabled: isLoaded && isSignedIn === true,
  });

  const defaultQuery = useQuery({
    queryKey: DEFAULT_ASSISTANT_IDENTITY_QUERY_KEY,
    queryFn: () => getDefaultAssistantIdentity(),
    enabled: isLoaded && isSignedIn !== true,
  });

  if (isSignedIn) {
    const personality = (memberQuery.data as ActivePersonalityResponse | undefined)
      ?.assistantPersonality;
    return {
      name: personality?.name ?? null,
      icon: personality?.icon ?? null,
      isLoading: memberQuery.isLoading,
    };
  }

  return {
    name: defaultQuery.data?.name ?? null,
    icon: defaultQuery.data?.icon ?? null,
    isLoading: !isLoaded || defaultQuery.isLoading,
  };
}
